import { Box, Flex, IconButton } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { Icons } from '../../shared/Icons';
import { FileUploads } from '../../shared/dropzone/FileUploads';
import { FileLink } from '@texas/api/endpoints/filesApi';
import { useFileUploads } from '@texas/api/hooks/useFileUploads';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createEditor,
  Editor,
  Element as SlateElement,
  Range,
  Transforms,
} from 'slate';
import {
  DefaultElement,
  Editable,
  RenderElementProps,
  Slate,
  withReact,
} from 'slate-react';
import React from 'react';
import {
  compareEnum,
  convertToEnum,
  ensureEnumNumber,
} from '@texas/utils/helpers/enumHelpers';
import { useSelectEntity } from './useSelectEntity/useSelectEntity';
import {
  appendEditorContent,
  createChunks,
  getFromPath,
  insertEntity,
  insertEntityCategory,
  previousNode,
  replaceEditorContent,
  resetEditorContent,
  slateEntity,
} from '../utils';
import { EntityValue } from '../renders/EntityValue';
import { EntityCategory } from '../renders/EntityCategory';
import { SlatePopover } from '../SlatePopover';
import { EntityItem, filtersForEntityType } from '../types';
import { useDebouncedCallback } from 'use-debounce';
import { EntityBadge } from '../EntityBadge';
import { CustomEditor, CustomElement } from '../extensions';
import { RichTextChunk } from '@texas/api/endpoints/chat/richTextTypes';
import { useDropzone } from 'react-dropzone';
import { handlePaste, sizeValidator } from '../../shared/dropzone/shared';
import { useTranslation } from 'react-i18next';
import { fadeInBottomAnimation } from '@texas/resources/animations/animations';
import { aiAcceptedImageFormats } from '@texas/utils/helpers/filesHelper';
import { InlineFile } from '@texas/api/endpoints/chat/messageTypes';
import { ChatMessageEntityLinkType } from '@texas/api/endpoints/chat/chatApi';
import { aiEvents } from '../events';
import { useLinkedEntities } from './useLinkedEntities';
import { randomIdentifier } from '@texas/utils/helpers/commonHelpers';

interface FormData {
  msg: string;
  entities: EntityItem[];
}

type SlatePopoverState = 'category' | 'entity';

const initialValue: CustomElement[] = [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
];

interface PromptProps {
  isDisabled?: boolean;
  multipleFilesSupport: boolean;
  onSubmit: (
    chunks: RichTextChunk[],
    attachedFiles: InlineFile[],
    rawText: string,
  ) => void;
}

export function PromptInput(props: PromptProps) {
  const { t } = useTranslation();
  const [attachedFiles, setAttachedFiles] = useState<InlineFile[]>([]);

  const { handleSubmit } = useForm<FormData>();

  const [slatePopoverState, setSlatePopoverState] =
    useState<SlatePopoverState>('category');
  const [entityItemSelectIndex, setEntityItemSelectIndex] = useState<number>(0);
  const [target, setTarget] = useState<Range | null>(null);
  const [category, setCategory] = useState<ChatMessageEntityLinkType | null>(
    null,
  );
  const [query, setQuery] = useState('');

  const onChangeDebounced = useDebouncedCallback((text: string) => {
    setQuery(text);
  }, 300);
  const { filterEntities, filterTypes, loading, groups, totalEntities } =
    useSelectEntity();

  const editor = useMemo(() => withEntities(withReact(createEditor())), []);

  const {
    linkedEntities,
    linkedEntitiesArray,
    addEntity,
    removeEntity,
    clearLinkedEntities,
  } = useLinkedEntities(editor);

  useEffect(() => {
    switch (slatePopoverState) {
      case 'category': {
        filterTypes(query);
        break;
      }
      case 'entity': {
        if (!category) {
          return;
        }

        const filtersToUse =
          filtersForEntityType[
            ensureEnumNumber(ChatMessageEntityLinkType, category)
          ];
        const filters =
          filtersToUse.length > 0
            ? filtersToUse.reduce<{ [key: string]: number[] }>(function (r, e) {
                r[e] = linkedEntities[e].map((x) => x.id);
                return r;
              }, {})
            : {};

        filterEntities({ query, filters: filters }, category);
      }
    }
  }, [
    query,
    slatePopoverState,
    category,
    filterEntities,
    linkedEntities,
    filterTypes,
  ]);

  useEffect(() => {
    const unsub = aiEvents.updateMessage.subscribe((data) => {
      clearLinkedEntities();
      replaceEditorContent(editor, data);
    });
    const unsub2 = aiEvents.appendEntity.subscribe((e) => {
      appendEditorContent(editor, slateEntity(e.item, e.type));
      addEntity(e.type, e.item);
    });

    return () => {
      unsub();
      unsub2();
    };
  }, [addEntity, clearLinkedEntities, editor]);

  function setNewState(newState: SlatePopoverState) {
    if (slatePopoverState === newState) return;

    setSlatePopoverState(newState);
  }

  const renderElement = useCallback((props: RenderElementProps) => {
    switch (props.element.type) {
      case 'entity-category':
        return <EntityCategory {...props} element={props.element} />;
      case 'entity-value':
        return (
          <EntityValue element={props.element} attributes={props.attributes}>
            {props.children}
          </EntityValue>
        );
      default:
        return <DefaultElement {...props} />;
    }
  }, []);

  const onUploadcomplete = useCallback((file: FileLink) => {
    setAttachedFiles((s) => [
      ...s,
      {
        fileId: file.id,
        previewIdentifier: file.previewIdentifier,
        extension: file.extension,
        fileName: file.name,
      },
    ]);
  }, []);
  const { fileUploads, uploadFiles, removeFileUpload, resetFileUploads } =
    useFileUploads(onUploadcomplete);
  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop: (acceptedFiles) => {
      uploadFiles(acceptedFiles, null);
    },
    useFsAccessApi: false,
    noClick: true,
    multiple: props.multipleFilesSupport,
    accept: aiAcceptedImageFormats,
    validator: (file) => sizeValidator(file, t),
  });

  const onSubmit = useCallback(() => {
    const { chunks, rawText } = createChunks(editor);

    if (rawText.length === 0) {
      return;
    }

    props.onSubmit(chunks, attachedFiles, rawText);
    resetEditorContent(editor);
    setAttachedFiles([]);
    resetFileUploads();
    clearLinkedEntities();
  }, [attachedFiles, clearLinkedEntities, editor, props, resetFileUploads]);

  const makeSlatePopoverSelection = useCallback(
    (item: EntityItem) => {
      if (!target) return;
      Transforms.select(editor, target);
      switch (slatePopoverState) {
        case 'category': {
          const cat = convertToEnum(ChatMessageEntityLinkType, item.id);
          setCategory(ensureEnumNumber(ChatMessageEntityLinkType, cat));
          insertEntityCategory(editor, cat);
          setSlatePopoverState('entity');
          break;
        }
        case 'entity': {
          if (!category) {
            setCategory(null);
            setSlatePopoverState('category');
            break;
          }

          item.identifier = randomIdentifier();
          insertEntity(editor, category, item.id, item.name, item.identifier);
          addEntity(category, item);
          setCategory(null);
          setSlatePopoverState('category');
          break;
        }
      }

      setTarget(null);
    },
    [addEntity, category, editor, slatePopoverState, target],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (target && totalEntities > 0) {
        switch (event.key) {
          case 'ArrowDown': {
            event.preventDefault();
            const prevIndex =
              entityItemSelectIndex >= totalEntities - 1
                ? 0
                : entityItemSelectIndex + 1;
            setEntityItemSelectIndex(prevIndex);
            break;
          }
          case 'ArrowUp': {
            event.preventDefault();
            const nextIndex =
              entityItemSelectIndex <= 0
                ? totalEntities - 1
                : entityItemSelectIndex - 1;
            setEntityItemSelectIndex(nextIndex);
            break;
          }
          case 'Tab':
          case 'Enter': {
            event.preventDefault();
            const items = Object.values(groups).flatMap((g) => g.items);
            makeSlatePopoverSelection(items[entityItemSelectIndex]);
            break;
          }
          case 'Escape': {
            event.preventDefault();
            setTarget(null);
            break;
          }
        }
      } else {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          handleSubmit(onSubmit)();
        }
      }
    },
    [
      target,
      totalEntities,
      entityItemSelectIndex,
      groups,
      makeSlatePopoverSelection,
      handleSubmit,
      onSubmit,
    ],
  );

  return (
    <Flex
      flexDir="column"
      gap={2}
      w="full"
      pos="sticky"
      bottom={8}
      p={4}
      maxW="800px"
      marginX="auto"
      animation={fadeInBottomAnimation()}
    >
      <Flex flexDir="column" gap={1}>
        <Flex gap={2} flexWrap="wrap">
          {linkedEntitiesArray.map((x) => {
            return (
              <EntityBadge
                key={`entity-${x.type}-${x.item.id}-${x.item.identifier}`}
                entity={x.item}
                type={convertToEnum(ChatMessageEntityLinkType, x.type)}
                onRemove={() =>
                  removeEntity(
                    convertToEnum(ChatMessageEntityLinkType, x.type),
                    x.item.identifier,
                  )
                }
              />
            );
          })}
        </Flex>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Flex
            flexDir="column"
            boxShadow="md"
            bg="texas.bg.800"
            _light={{ bg: 'gray.10', borderColor: 'gray.50' }}
            border="1px solid"
            borderColor="gray.800"
            borderRadius="md"
            {...getRootProps()}
          >
            <Box p={2}>
              <Box pb={2}>
                <FileUploads
                  fileUploads={fileUploads}
                  removeFileUpload={removeFileUpload}
                  onRemove={(file) => {
                    removeFileUpload(file);

                    if (file.type === 'uploaded' && file.id) {
                      setAttachedFiles((prev) =>
                        prev.filter((f) => f.fileId !== file.id),
                      );
                    }
                  }}
                />
              </Box>
              <Slate
                editor={editor}
                initialValue={initialValue}
                onChange={() => {
                  const { selection } = editor;

                  for (const op of editor.operations) {
                    if (
                      op.type === 'remove_node' &&
                      SlateElement.isElement(op.node)
                    ) {
                      if (op.node.type === 'entity-value') {
                        removeEntity(op.node.entityType, op.node.identifier);
                      }
                    }
                  }

                  if (selection && Range.isCollapsed(selection)) {
                    const [startSelection] = Range.edges(selection);
                    const wordBefore = Editor.before(editor, startSelection, {
                      unit: 'word',
                    });
                    const before =
                      wordBefore && Editor.before(editor, wordBefore);
                    const beforeRange =
                      before && Editor.range(editor, before, startSelection);
                    const beforeText =
                      beforeRange && Editor.string(editor, beforeRange);

                    const prevNode = previousNode(editor, selection.focus.path);
                    const currentNode = getFromPath(
                      editor,
                      selection.focus.path,
                    );

                    if (prevNode && prevNode.node.type === 'entity-category') {
                      setNewState('entity');
                      if (
                        !compareEnum(
                          ChatMessageEntityLinkType,
                          prevNode.node.entityType,
                          category,
                        )
                      ) {
                        setCategory(
                          ensureEnumNumber(
                            ChatMessageEntityLinkType,
                            prevNode.node.entityType,
                          ),
                        );
                      }
                      onChangeDebounced(currentNode.text.trim());
                      setTarget(beforeRange!);
                    } else {
                      setNewState('category');

                      const beforeMatch = beforeText?.match(/^@(\w+)$/);
                      const currentNodeMatch = currentNode.text
                        .trim()
                        .match(/^@|\s+@$/);
                      if (beforeMatch) {
                        setTarget(beforeRange!); // anchor the category menu to this range
                        setQuery(beforeMatch[1]); // filter categories as they type
                      } else if (currentNodeMatch) {
                        const wb = Editor.before(editor, selection);
                        const wbr =
                          wb && Editor.range(editor, wb, startSelection);
                        setTarget(wbr!);
                        setQuery('');
                      } else {
                        setTarget(null);
                      }
                    }

                    setEntityItemSelectIndex(0);
                    return;
                  }

                  setTarget(null);
                }}
              >
                <Editable
                  renderElement={renderElement}
                  placeholder={t('ai.promptPlaceholder')}
                  onKeyDown={onKeyDown}
                  onPaste={async (e) => {
                    await handlePaste(
                      e.clipboardData,
                      t,
                      (f) => {
                        uploadFiles(f, null);
                      },
                      false,
                      aiAcceptedImageFormats,
                    );
                  }}
                />
                <SlatePopover
                  show={!!target}
                  values={groups}
                  totalEntities={totalEntities}
                  loading={loading}
                  index={entityItemSelectIndex}
                  showHoverCard={slatePopoverState === 'entity'}
                  onClick={(i) => {
                    makeSlatePopoverSelection(i);
                  }}
                />
              </Slate>
            </Box>

            <Flex gap={1} justifyContent="space-between" p={1}>
              <IconButton
                w="fit-content"
                variant="ghost"
                size="sm"
                aria-label="Upload"
                onClick={open}
                icon={<Icons.FilePlusOutline />}
              />
              <IconButton
                type="submit"
                ml="auto"
                w="fit-content"
                variant="ghost"
                size="sm"
                isDisabled={props.isDisabled}
                aria-label="Send"
                icon={<Icons.Send />}
              />
            </Flex>
            <input multiple={props.multipleFilesSupport} {...getInputProps()} />
          </Flex>
        </form>
      </Flex>
    </Flex>
  );
}

const withEntities = (editor: CustomEditor) => {
  const { isInline, isVoid, markableVoid } = editor;

  editor.isInline = (el: SlateElement) => {
    return (
      el.type === 'entity-category' ||
      el.type === 'entity-value' ||
      isInline(el)
    );
  };
  editor.isVoid = (el: SlateElement) => {
    return (
      el.type === 'entity-category' || el.type === 'entity-value' || isVoid(el)
    );
  };
  editor.markableVoid = (el: SlateElement) => {
    return (
      el.type === 'entity-category' ||
      el.type === 'entity-value' ||
      markableVoid(el)
    );
  };

  return editor;
};
